import type { IQuiz, IAttemptResult, IUserAnswer, IAnswerReveal } from '~/components/widget/quiz/types'

interface StartAttemptResponse {
  status: string
  message: string
  data: { attempt_id: string | null, guest_token?: string | null }
}

interface QuizResponse {
  data: IQuiz
}

interface ResultsResponse {
  status: string
  message: string
  data: IAttemptResult
}

/**
 * A guest attempt is reachable only with the secret token the API issues when it starts.
 * Tokens are kept per quiz in localStorage, so a guest can continue after a reload.
 */
const GUEST_TOKENS_KEY = 'quiz_guest_tokens'

type GuestTokens = Record<string, string>

const readGuestTokens = (): GuestTokens => {
  if (!import.meta.client) return {}
  try {
    const parsed = JSON.parse(localStorage.getItem(GUEST_TOKENS_KEY) ?? '{}')
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const writeGuestTokens = (tokens: GuestTokens): void => {
  if (!import.meta.client) return
  try {
    if (Object.keys(tokens).length) {
      localStorage.setItem(GUEST_TOKENS_KEY, JSON.stringify(tokens))
    } else {
      localStorage.removeItem(GUEST_TOKENS_KEY)
    }
  } catch {
    // Storage can be blocked by the browser; the attempt then lasts until the page is closed
  }
}

const isForbidden = (err: unknown): boolean =>
  (err as { statusCode?: number, status?: number })?.statusCode === 403
  || (err as { status?: number })?.status === 403

export const useQuiz = (quizId: number) => {
  const { $api } = useNuxtApp()
  const fingerprintStore = useFingerprintStore()

  const quiz = ref<IQuiz | null>(null)
  const questions = ref<IQuiz['questions']>([])
  const attemptId = ref<string | null>(null)
  const guestToken = ref<string | null>(readGuestTokens()[quizId] ?? null)

  const saveGuestToken = (token: string | null): void => {
    guestToken.value = token
    const tokens = Object.fromEntries(
      Object.entries(readGuestTokens()).filter(([id]) => id !== String(quizId))
    )
    writeGuestTokens(token ? { ...tokens, [quizId]: token } : tokens)
  }

  const ensureFingerprint = async () => {
    if (!fingerprintStore.loaded) await fingerprintStore.initializeFingerprint()
  }

  /** The token is a secret: it goes in a header, never in the URL, so it stays out of access logs. */
  const guestHeaders = (): Record<string, string> =>
    guestToken.value ? { 'X-Quiz-Guest-Token': guestToken.value } : {}

  /** A token the API no longer accepts (claimed, or from another device) is dropped, so the guest can start over. */
  const withGuestToken = async <T>(request: () => Promise<T>): Promise<T> => {
    try {
      return await request()
    } catch (err) {
      if (isForbidden(err) && guestToken.value) saveGuestToken(null)
      throw err
    }
  }

  const checkAttempt = async (): Promise<boolean> => {
    guestToken.value = readGuestTokens()[quizId] ?? null
    const response = await $api<StartAttemptResponse>(`/quizzes/${quizId}/attempts`, {
      headers: guestHeaders(),
    })
    if (response.data.attempt_id) {
      attemptId.value = response.data.attempt_id
      return true
    }
    return false
  }

  const startAttempt = async (): Promise<void> => {
    await ensureFingerprint()

    const response = await $api<StartAttemptResponse>(`/quizzes/${quizId}/attempts`, {
      method: 'POST',
      body: fingerprintStore.visitorId ? { fingerprint: fingerprintStore.visitorId } : {},
    })

    attemptId.value = response.data.attempt_id
    saveGuestToken(response.data.guest_token ?? null)
  }

  const fetchQuestion = async (questionId?: number) => {
    if (!attemptId.value) return

    const url = useUrlWithParams(`/quizzes/${quizId}`, {
      attempt_id: attemptId.value,
      ...(questionId != null ? { question_id: questionId } : {}),
    })
    const response = await withGuestToken(() => $api<QuizResponse>(url, { headers: guestHeaders() }))
    quiz.value = response.data
    questions.value = response.data.questions
  }

  /** Returns the correct answer and its explanation for a question with options, null for a text question. */
  const submitAnswer = async (answer: IUserAnswer): Promise<IAnswerReveal | null> => {
    if (!attemptId.value) return null

    const response = await withGuestToken(() => $api<{ data: IAnswerReveal | null }>(`/attempts/${attemptId.value}/answers`, {
      method: 'POST',
      headers: guestHeaders(),
      body: answer,
    }))

    return response?.data?.correct_answer_ids ? response.data : null
  }

  const getResults = async (): Promise<IAttemptResult> => {
    if (!attemptId.value) throw new Error('No active attempt')

    const response = await withGuestToken(() => $api<ResultsResponse>(`/attempts/${attemptId.value}/results`, {
      headers: guestHeaders(),
    }))
    return response.data
  }

  /** After login, link the guest attempts started in this browser to the account. */
  const claimGuestAttempts = async (): Promise<void> => {
    const tokens = readGuestTokens()
    const guestTokens = Object.values(tokens)
    if (!guestTokens.length) return

    await $api('/claim-guest-attempts', {
      method: 'POST',
      body: { guest_tokens: guestTokens },
    })

    writeGuestTokens({})
    guestToken.value = null
  }

  const resetAttempt = (): void => {
    quiz.value = null
    questions.value = []
    attemptId.value = null
  }

  return {
    quiz,
    questions,
    checkAttempt,
    startAttempt,
    fetchQuestion,
    submitAnswer,
    getResults,
    claimGuestAttempts,
    resetAttempt,
  }
}

export default useQuiz
